import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MainLayout from '../../components/layout/MainLayout';
import { hostApi, Host } from '../../services/api/host.api';
import infrastructureMetricApi, { TimeSeriesData } from '../../services/api/infrastructure-metric.api';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';
import ProcessList from '../../components/infrastructure/ProcessList';

export default function HostDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['infrastructure', 'common']);

  const [activeTab, setActiveTab] = useState<'metrics' | 'processes'>(
    (searchParams.get('tab') as 'metrics' | 'processes') || 'metrics'
  );
  const [host, setHost] = useState<Host | null>(null);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [currentMetrics, setCurrentMetrics] = useState<any>(null);
  const [cpuData, setCpuData] = useState<TimeSeriesData[]>([]);
  const [memoryData, setMemoryData] = useState<TimeSeriesData[]>([]);
  const [diskReadData, setDiskReadData] = useState<TimeSeriesData[]>([]);
  const [diskWriteData, setDiskWriteData] = useState<TimeSeriesData[]>([]);
  const [networkRxData, setNetworkRxData] = useState<TimeSeriesData[]>([]);
  const [networkTxData, setNetworkTxData] = useState<TimeSeriesData[]>([]);
  const [loadAvg1Data, setLoadAvg1Data] = useState<TimeSeriesData[]>([]);
  const [loadAvg5Data, setLoadAvg5Data] = useState<TimeSeriesData[]>([]);
  const [loadAvg15Data, setLoadAvg15Data] = useState<TimeSeriesData[]>([]);

  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 1 * 60 * 60 * 1000, // Last 1 hour
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '1h',
  });

  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({
    enabled: false,
    interval: 30000, // 30 seconds
  });

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (id) {
      loadHost();
      loadMetrics();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadMetrics();
    }
  }, [timeRange]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh.enabled || !id) return;

    const interval = setInterval(() => {
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const endTime = Date.now();
        const ranges: Record<string, number> = {
          '15m': 15 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '6h': 6 * 60 * 60 * 1000,
          '12h': 12 * 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
        };
        const startTime = endTime - (ranges[timeRange.presetValue] || ranges['12h']);
        setTimeRange({ ...timeRange, startTime, endTime });
      } else {
        loadMetrics();
      }
    }, autoRefresh.interval);

    return () => clearInterval(interval);
  }, [autoRefresh, timeRange]);

  const loadHost = async () => {
    try {
      setLoading(true);
      const hostData = await hostApi.getHost(id!);
      setHost(hostData);
    } catch (error) {
      console.error('Error loading host:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    if (!id) return;

    try {
      setMetricsLoading(true);

      // Load current metrics
      try {
        const currentMetrics = await infrastructureMetricApi.getCurrent(id);
        console.log('Current metrics response:', currentMetrics);
        // API now returns { success: true, data: {...} }
        setCurrentMetrics(currentMetrics.data || currentMetrics);
      } catch (error) {
        console.error('Error loading current metrics:', error);
      }

      // Load time series for charts
      console.log('Loading time series from', new Date(timeRange.startTime), 'to', new Date(timeRange.endTime));
      const [cpu, memory, diskRead, diskWrite, netRx, netTx, load1, load5, load15] = await Promise.allSettled([
        infrastructureMetricApi.getTimeSeries(id, 'cpu_usage_percent', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'memory_usage_percent', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'disk_read_bytes', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'disk_write_bytes', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'network_rx_bytes', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'network_tx_bytes', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'load_avg_1m', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'load_avg_5m', timeRange.startTime, timeRange.endTime),
        infrastructureMetricApi.getTimeSeries(id, 'load_avg_15m', timeRange.startTime, timeRange.endTime),
      ]);

      if (cpu.status === 'fulfilled') {
        // API now returns { success: true, data: [...] }, extract the array
        const cpuArray = cpu.value.data || cpu.value;
        console.log('CPU data loaded:', cpuArray.length, 'points');
        setCpuData(cpuArray);
      } else if (cpu.status === 'rejected') {
        console.error('CPU load failed:', cpu.reason);
      }

      if (memory.status === 'fulfilled') {
        const memoryArray = memory.value.data || memory.value;
        console.log('Memory data loaded:', memoryArray.length, 'points');
        setMemoryData(memoryArray);
      }
      if (diskRead.status === 'fulfilled') {
        setDiskReadData(diskRead.value.data || diskRead.value);
      }
      if (diskWrite.status === 'fulfilled') {
        setDiskWriteData(diskWrite.value.data || diskWrite.value);
      }
      if (netRx.status === 'fulfilled') {
        setNetworkRxData(netRx.value.data || netRx.value);
      }
      if (netTx.status === 'fulfilled') {
        setNetworkTxData(netTx.value.data || netTx.value);
      }
      if (load1.status === 'fulfilled') {
        setLoadAvg1Data(load1.value.data || load1.value);
      }
      if (load5.status === 'fulfilled') {
        setLoadAvg5Data(load5.value.data || load5.value);
      }
      if (load15.status === 'fulfilled') {
        setLoadAvg15Data(load15.value.data || load15.value);
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setMetricsLoading(false);
      setLastUpdated(new Date());
    }
  };

  const formatBytes = (bytes: number | string) => {
    const numBytes = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (numBytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleTabChange = (tab: 'metrics' | 'processes') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  if (!host) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('infrastructure:details.notFound')}</h3>
          <button
            onClick={() => navigate('/infrastructure/hosts')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {t('infrastructure:details.backToInfrastructure')}
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/infrastructure/hosts')}
              className="mb-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {t('infrastructure:details.backToInfrastructure')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{host.hostname}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{host.ip_address}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              host.status === 'online' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
              host.status === 'offline' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            }`}>
              {t(`infrastructure:details.${host.status}`)}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('metrics')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'metrics'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('infrastructure:details.tabs.metrics')}
              </div>
            </button>
            <button
              onClick={() => handleTabChange('processes')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'processes'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {t('infrastructure:details.tabs.processes')}
              </div>
            </button>
          </nav>
        </div>

        {/* Time Range and Auto Refresh Controls - Only show on metrics tab */}
        {activeTab === 'metrics' && (
          <div className="flex flex-wrap items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            <AutoRefreshToggle
              value={autoRefresh}
              onChange={setAutoRefresh}
              onManualRefresh={loadMetrics}
              lastUpdated={lastUpdated || undefined}
            />
          </div>
        )}

        {/* Metrics Tab Content */}
        {activeTab === 'metrics' && (
          <>
        {/* Current Metrics Cards */}
        {currentMetrics && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-blue-50 to-white p-6 shadow-sm dark:border-gray-800 dark:from-blue-900/20 dark:to-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('infrastructure:details.metrics.cpuUsage')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    {currentMetrics.cpu_usage_percent?.toFixed(1)}%
                  </p>
                </div>
                <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-900/40">
                  <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-green-50 to-white p-6 shadow-sm dark:border-gray-800 dark:from-green-900/20 dark:to-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('infrastructure:details.metrics.memoryUsage')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    {currentMetrics.memory_usage_percent?.toFixed(1)}%
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {formatBytes(currentMetrics.memory_used_bytes)} / {formatBytes(currentMetrics.memory_total_bytes)}
                  </p>
                </div>
                <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/40">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-purple-50 to-white p-6 shadow-sm dark:border-gray-800 dark:from-purple-900/20 dark:to-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('infrastructure:details.metrics.diskUsage')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    {currentMetrics.disk_usage_percent?.toFixed(1)}%
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {formatBytes(currentMetrics.disk_used_bytes)} / {formatBytes(currentMetrics.disk_total_bytes)}
                  </p>
                </div>
                <div className="rounded-full bg-purple-100 p-3 dark:bg-purple-900/40">
                  <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-orange-50 to-white p-6 shadow-sm dark:border-gray-800 dark:from-orange-900/20 dark:to-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('infrastructure:details.metrics.loadAverage')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    {currentMetrics.load_avg_1m?.toFixed(2)}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    5m: {currentMetrics.load_avg_5m?.toFixed(2)} | 15m: {currentMetrics.load_avg_15m?.toFixed(2)}
                  </p>
                </div>
                <div className="rounded-full bg-orange-100 p-3 dark:bg-orange-900/40">
                  <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* CPU Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{t('infrastructure:details.charts.cpuTitle')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={cpuData}>
                <defs>
                  <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="time" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#ffffff' }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Area type="monotone" dataKey="value" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCpu)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Memory Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{t('infrastructure:details.charts.memoryTitle')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={memoryData}>
                <defs>
                  <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="time" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#ffffff' }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Area type="monotone" dataKey="value" stroke="#10b981" fillOpacity={1} fill="url(#colorMemory)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Disk I/O Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{t('infrastructure:details.charts.diskIOTitle')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={diskReadData.length > 0 ? diskReadData : diskWriteData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="timestamp" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(ts) => new Date(ts).toLocaleTimeString()} stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#ffffff' }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                  labelFormatter={(ts) => new Date(ts).toLocaleString()}
                />
                <Legend />
                <Line type="monotone" data={diskReadData} dataKey="value" stroke="#8b5cf6" name={t('infrastructure:details.charts.read')} strokeWidth={2} dot={false} />
                <Line type="monotone" data={diskWriteData} dataKey="value" stroke="#ec4899" name={t('infrastructure:details.charts.write')} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Network Traffic Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{t('infrastructure:details.charts.networkTrafficTitle')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={networkRxData.length > 0 ? networkRxData : networkTxData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="timestamp" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(ts) => new Date(ts).toLocaleTimeString()} stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#ffffff' }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                  labelFormatter={(ts) => new Date(ts).toLocaleString()}
                />
                <Legend />
                <Line type="monotone" data={networkRxData} dataKey="value" stroke="#06b6d4" name={t('infrastructure:details.charts.rx')} strokeWidth={2} dot={false} />
                <Line type="monotone" data={networkTxData} dataKey="value" stroke="#f59e0b" name={t('infrastructure:details.charts.tx')} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Load Average Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900 lg:col-span-2">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{t('infrastructure:details.charts.loadAvgTitle')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={loadAvg1Data.length > 0 ? loadAvg1Data : (loadAvg5Data.length > 0 ? loadAvg5Data : loadAvg15Data)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="timestamp" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(ts) => new Date(ts).toLocaleTimeString()} stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#ffffff' }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                  labelFormatter={(ts) => new Date(ts).toLocaleString()}
                />
                <Legend />
                <Line type="monotone" data={loadAvg1Data} dataKey="value" stroke="#ef4444" name={t('infrastructure:details.charts.oneMin')} strokeWidth={2} dot={false} />
                <Line type="monotone" data={loadAvg5Data} dataKey="value" stroke="#f59e0b" name={t('infrastructure:details.charts.fiveMin')} strokeWidth={2} dot={false} />
                <Line type="monotone" data={loadAvg15Data} dataKey="value" stroke="#10b981" name={t('infrastructure:details.charts.fifteenMin')} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
          </>
        )}

        {/* Processes Tab Content */}
        {activeTab === 'processes' && id && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <ProcessList hostId={id} autoRefresh={true} refreshInterval={30000} />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
